import { put, takeEvery, takeLatest, call, all, delay, fork } from "redux-saga/effects";
import { toast } from "react-toastify";

import { types } from "../../Types/agent";
import {
  fetchAgent,
  saveAgent,
  saveAgentExample,
  runAgentTest,
  fetchInboxMessages,
  fetchDraftStats,
  requestDraftGeneration,
  fetchDraft,
  patchDraft,
  postDraftDecision,
  postDraftSend,
  postDraftVideo,
  fetchMailboxWatch,
  startMailboxWatch,
  stopMailboxWatch,
  fetchAvatars,
  fetchStockFaces,
  postAvatar,
  fetchAvatar,
  postDefaultAvatar,
  removeAvatar,
} from "./api";

type SagaGenerator<T = any> = Generator<any, T, any>;

export const errorMessage = (error: any, fallback: string) => {
  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.message) return error.message;
  return fallback;
};

// The agent's readiness may come on the agent itself or next to it.
const withReadiness = (result: any) =>
  result?.agent ? { ...result.agent, readiness: result.agent.readiness || result.readiness } : null;

function* getAgentSaga(): SagaGenerator<void> {
  try {
    const result = yield call(fetchAgent);
    yield put({ type: types.GET_AGENT_SUCCESS, payload: withReadiness(result) });
  } catch (error: any) {
    const message = errorMessage(error, "Failed to load the agent");
    yield put({ type: types.GET_AGENT_FAILURE, payload: message });
    toast.error(message);
  }
}

function* updateAgentSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(saveAgent, action.payload);
    yield put({ type: types.UPDATE_AGENT_SUCCESS, payload: withReadiness(result) });
    toast.info("Agent saved");
  } catch (error: any) {
    yield put({ type: types.UPDATE_AGENT_FAILURE });
    toast.error(errorMessage(error, "Failed to save the agent"));
  }
}

function* addAgentExampleSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(saveAgentExample, action.payload);
    yield put({ type: types.ADD_AGENT_EXAMPLE_SUCCESS, payload: withReadiness(result) });
    toast.info("Example added");
  } catch (error: any) {
    yield put({ type: types.ADD_AGENT_EXAMPLE_FAILURE });
    toast.error(errorMessage(error, "Failed to add the example"));
  }
}

function* removeAgentExampleSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(saveAgent, { approvedExamples: action.payload.approvedExamples });
    yield put({ type: types.REMOVE_AGENT_EXAMPLE_SUCCESS, payload: withReadiness(result) });
    toast.info("Example removed");
  } catch (error: any) {
    yield put({ type: types.REMOVE_AGENT_EXAMPLE_FAILURE });
    toast.error(errorMessage(error, "Failed to remove the example"));
  }
}

function* testAgentSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(runAgentTest, action.payload);
    yield put({ type: types.TEST_AGENT_SUCCESS, payload: result });
  } catch (error: any) {
    const message = errorMessage(error, "Agent test failed");
    yield put({ type: types.TEST_AGENT_FAILURE, payload: message });
    toast.error(message);
  }
}

function* getInboxMessagesSaga(): SagaGenerator<void> {
  try {
    const result = yield call(fetchInboxMessages);
    yield put({ type: types.GET_INBOX_MESSAGES_SUCCESS, payload: result.messages });
  } catch (error: any) {
    const message = errorMessage(error, "Failed to load the inbox");
    yield put({ type: types.GET_INBOX_MESSAGES_FAILURE, payload: message });
    toast.error(message);
  }
}

function* getDraftStatsSaga(): SagaGenerator<void> {
  try {
    const result = yield call(fetchDraftStats);
    yield put({ type: types.GET_DRAFT_STATS_SUCCESS, payload: result });
  } catch (error: any) {
    // Stats are informational only; no toast.
    yield put({ type: types.GET_DRAFT_STATS_FAILURE });
  }
}

// Runs one draft endpoint, stores the returned { draft } and clears the
// in-flight key. On failure it toasts and re-reads the draft so the screen
// shows the server's real state (e.g. "failed" after a send error, or the
// already-running video after a 409).
function* draftRequest(
  key: string,
  successType: string,
  failureType: string,
  request: () => Promise<any>,
  fallbackError: string,
  successToast?: (draft: any) => string | null
): SagaGenerator<boolean> {
  try {
    const result = yield call(request);
    yield put({ type: successType, payload: { key, draft: result.draft } });
    const text = successToast ? successToast(result.draft) : null;
    if (text) toast.success(text);
    yield put({ type: types.GET_DRAFT_STATS });
    return true;
  } catch (error: any) {
    const message = errorMessage(error, fallbackError);
    yield put({ type: failureType, payload: { key, error: message } });
    toast.error(message);
    yield put({ type: types.FETCH_DRAFT, payload: { id: key } });
    return false;
  }
}

// POST /ai/drafts/generate answers either { draft, reused } (existing draft)
// or 202 { status: "queued" } (written in the background; the Inbox polls
// GET /ai/inbox/messages while message.draftPending is true).
function* generateDraftSaga(action: any): SagaGenerator<void> {
  const { inboundMessageId, force } = action.payload;
  try {
    const result = yield call(
      requestDraftGeneration,
      force ? { inboundMessageId, force: true } : { inboundMessageId }
    );
    if (result && result.draft) {
      yield put({ type: types.GENERATE_DRAFT_SUCCESS, payload: { key: inboundMessageId, draft: result.draft } });
      yield put({ type: types.GET_DRAFT_STATS });
    } else {
      yield put({ type: types.GENERATE_DRAFT_QUEUED, payload: { key: inboundMessageId } });
      toast.info("Writing a reply. This usually takes under a minute.");
    }
  } catch (error: any) {
    const message = errorMessage(error, "Failed to generate a draft");
    yield put({ type: types.GENERATE_DRAFT_FAILURE, payload: { key: inboundMessageId, error: message } });
    toast.error(message);
  }
}

function* fetchDraftSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(fetchDraft, action.payload.id);
    yield put({ type: types.FETCH_DRAFT_SUCCESS, payload: { key: action.payload.id, draft: result.draft } });
  } catch (error: any) {
    // Background refresh/poll: stay quiet, the next poll retries.
    yield put({ type: types.FETCH_DRAFT_FAILURE, payload: { key: action.payload.id } });
  }
}

function* updateDraftBodySaga(action: any): SagaGenerator<void> {
  const { id, body } = action.payload;
  yield call(
    draftRequest,
    id,
    types.UPDATE_DRAFT_BODY_SUCCESS,
    types.UPDATE_DRAFT_BODY_FAILURE,
    () => patchDraft(id, body),
    "Failed to save the draft",
    () => "Draft saved"
  );
}

function* decideDraftSaga(action: any): SagaGenerator<void> {
  const { id, action: decision, body } = action.payload;
  yield call(
    draftRequest,
    id,
    types.DECIDE_DRAFT_SUCCESS,
    types.DECIDE_DRAFT_FAILURE,
    () => postDraftDecision(id, decision, body),
    "Failed to update the draft",
    () => (decision === "approve" ? "Draft approved" : "Draft rejected")
  );
}

function* sendDraftSaga(action: any): SagaGenerator<void> {
  const { id } = action.payload;
  yield call(
    draftRequest,
    id,
    types.SEND_DRAFT_SUCCESS,
    types.SEND_DRAFT_FAILURE,
    () => postDraftSend(id),
    "Failed to send the reply",
    (draft) => (draft?.status === "failed" ? null : "Reply sent")
  );
}

function* createDraftVideoSaga(action: any): SagaGenerator<void> {
  const { id, avatarId } = action.payload;
  yield call(
    draftRequest,
    id,
    types.CREATE_DRAFT_VIDEO_SUCCESS,
    types.CREATE_DRAFT_VIDEO_FAILURE,
    () => postDraftVideo(id, avatarId),
    "Failed to start the video",
    () => "Video generation started"
  );
}

function* getWatchSaga(): SagaGenerator<void> {
  try {
    const result = yield call(fetchMailboxWatch);
    yield put({ type: types.GET_WATCH_SUCCESS, payload: result });
  } catch (error: any) {
    // The inbox still works without live sync status.
    yield put({ type: types.GET_WATCH_FAILURE });
  }
}

// Backfill after enabling live sync runs asynchronously on the server, so
// re-read the inbox a couple of times.
function* refreshAfterBackfill(): SagaGenerator<void> {
  yield delay(15000);
  yield put({ type: types.GET_INBOX_MESSAGES });
  yield delay(30000);
  yield put({ type: types.GET_INBOX_MESSAGES });
  yield put({ type: types.GET_DRAFT_STATS });
}

function* startWatchSaga(): SagaGenerator<void> {
  try {
    yield call(startMailboxWatch);
    yield put({ type: types.START_WATCH_SUCCESS });
    toast.success("Live inbox sync enabled. Recent replies will appear shortly.");
    yield put({ type: types.GET_WATCH });
    yield fork(refreshAfterBackfill);
  } catch (error: any) {
    yield put({ type: types.START_WATCH_FAILURE });
    toast.error(errorMessage(error, "Failed to enable inbox sync. Reconnect Gmail first."));
  }
}

function* stopWatchSaga(): SagaGenerator<void> {
  try {
    yield call(stopMailboxWatch);
    yield put({ type: types.STOP_WATCH_SUCCESS });
    toast.info("Live inbox sync stopped");
  } catch (error: any) {
    yield put({ type: types.STOP_WATCH_FAILURE });
    toast.error(errorMessage(error, "Failed to stop inbox sync"));
  }
}

function* getAvatarsSaga(): SagaGenerator<void> {
  try {
    const result = yield call(fetchAvatars);
    yield put({ type: types.GET_AVATARS_SUCCESS, payload: result.avatars });
  } catch (error: any) {
    const message = errorMessage(error, "Failed to load avatars");
    yield put({ type: types.GET_AVATARS_FAILURE, payload: message });
    toast.error(message);
  }
}

function* getStockFacesSaga(): SagaGenerator<void> {
  try {
    const result = yield call(fetchStockFaces);
    yield put({ type: types.GET_STOCK_FACES_SUCCESS, payload: result.faces });
  } catch (error: any) {
    const message = errorMessage(error, "Failed to load stock presenters");
    yield put({ type: types.GET_STOCK_FACES_FAILURE, payload: message });
    toast.error(message);
  }
}

function* createAvatarSaga(action: any): SagaGenerator<void> {
  try {
    const result = yield call(postAvatar, action.payload);
    yield put({ type: types.CREATE_AVATAR_SUCCESS, payload: result.avatar });
    toast.success(
      result.avatar?.status === "training"
        ? "Avatar added. Training can take from a few minutes to a few hours."
        : "Avatar added"
    );
  } catch (error: any) {
    yield put({ type: types.CREATE_AVATAR_FAILURE });
    toast.error(errorMessage(error, "Failed to add the avatar"));
  }
}

function* syncAvatarSaga(action: any): SagaGenerator<void> {
  const { id } = action.payload;
  try {
    const result = yield call(fetchAvatar, id);
    yield put({ type: types.SYNC_AVATAR_SUCCESS, payload: result.avatar });
  } catch (error: any) {
    // Used by background polling; the next poll retries.
    yield put({ type: types.SYNC_AVATAR_FAILURE, payload: { id } });
  }
}

function* setDefaultAvatarSaga(action: any): SagaGenerator<void> {
  const { id } = action.payload;
  try {
    const result = yield call(postDefaultAvatar, id);
    yield put({ type: types.SET_DEFAULT_AVATAR_SUCCESS, payload: result.avatar });
    toast.success("Default avatar updated");
  } catch (error: any) {
    yield put({ type: types.SET_DEFAULT_AVATAR_FAILURE, payload: { id } });
    toast.error(errorMessage(error, "Failed to set the default avatar"));
  }
}

function* deleteAvatarSaga(action: any): SagaGenerator<void> {
  const { id } = action.payload;
  try {
    yield call(removeAvatar, id);
    yield put({ type: types.DELETE_AVATAR_SUCCESS, payload: id });
    toast.info("Avatar removed");
  } catch (error: any) {
    yield put({ type: types.DELETE_AVATAR_FAILURE, payload: { id } });
    toast.error(errorMessage(error, "Failed to remove the avatar"));
  }
}

export function* agentWatcher(): SagaGenerator<void> {
  yield all([
    takeLatest(types.GET_AGENT, getAgentSaga),
    takeEvery(types.UPDATE_AGENT, updateAgentSaga),
    takeEvery(types.ADD_AGENT_EXAMPLE, addAgentExampleSaga),
    takeEvery(types.REMOVE_AGENT_EXAMPLE, removeAgentExampleSaga),
    takeEvery(types.TEST_AGENT, testAgentSaga),
    takeLatest(types.GET_INBOX_MESSAGES, getInboxMessagesSaga),
    takeLatest(types.GET_DRAFT_STATS, getDraftStatsSaga),
    takeEvery(types.GENERATE_DRAFT, generateDraftSaga),
    takeEvery(types.FETCH_DRAFT, fetchDraftSaga),
    takeEvery(types.UPDATE_DRAFT_BODY, updateDraftBodySaga),
    takeEvery(types.DECIDE_DRAFT, decideDraftSaga),
    takeEvery(types.SEND_DRAFT, sendDraftSaga),
    takeEvery(types.CREATE_DRAFT_VIDEO, createDraftVideoSaga),
    takeLatest(types.GET_WATCH, getWatchSaga),
    takeEvery(types.START_WATCH, startWatchSaga),
    takeEvery(types.STOP_WATCH, stopWatchSaga),
    takeLatest(types.GET_AVATARS, getAvatarsSaga),
    takeLatest(types.GET_STOCK_FACES, getStockFacesSaga),
    takeEvery(types.CREATE_AVATAR, createAvatarSaga),
    takeEvery(types.SYNC_AVATAR, syncAvatarSaga),
    takeEvery(types.SET_DEFAULT_AVATAR, setDefaultAvatarSaga),
    takeEvery(types.DELETE_AVATAR, deleteAvatarSaga),
  ]);
}
